import type { CSSProperties } from 'react';
import { useComposerInset } from '@/components/contentPane/useComposerInset';
import { useChatSurfacePrefs } from '@/lib/chatSurfacePrefs';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDataStore } from '@/stores/dataStore';
import { canIn, PERMISSIONS } from '@/lib/guildPermissions';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { errToToast } from '@/lib/humanizeApiError';
import { HISTORY_PAGE, atStartAfterPage } from '@/lib/historyPaging';
import { getSocket } from '@/lib/socket';
import { joinChannelRoom } from '@/lib/channelRoom';
import { useGuildRoster } from '@/lib/useGuildRoster';
import type { Message } from '@/types/api';
import { asSavedChannelKind } from '@/components/contentPane/EmptyChannel';
import { isSelfVault } from '@/components/channelDrawer/primitives';
import {
  EMPTY_MESSAGE_LIST,
  MessagesList,
} from '@/components/contentPane/MessagesList';
import { Composer } from '@/components/contentPane/Composer';
import { useBlockedBy } from '@/lib/useBlockedBy';
import { VoiceChannelStub } from '@/components/contentPane/VoiceChannelStub';
import { HeroEmptyState } from '@/heroui/content/HeroEmptyState';
import { createReadMarkScheduler } from '@/lib/readMarkScheduler';

/** Coalesce a burst of arriving messages into one «read» for the peer. */
const READ_MARK_DELAY_MS = 500;
const SHARED_READ_MARK_DELAY_MS = 10_000;


/**
 * Format an mm:ss duration string from a millisecond value. Used by the
 * voice-recording HUD timer and the voice-note playback timer.
 */


// Narrows a runtime ChannelType down to the kinds we can actually bookmark
// from. Category / voice / thread channels never reach MessagesList in
// practice (the parent branches into VoiceChannelStub for voice, and the
// other two aren't selectable as a chat target), so the fallback to TEXT
// is a defensive no-op — it just keeps TS happy at the boundary.


/**
 * Phase-1.5 content pane: channel header with action-icons row, scrolling
 * message list with author-grouping and date separators, composer with
 * attach/emoji, auto-grow textarea, Shift+Enter for newline.
 *
 * Loads message history on channel switch, joins the channel WS room so
 * pushes flow in real-time, ticks a typing indicator while the user composes.
 */
export function ContentPane() {
  const surfaces = useChatSurfacePrefs();
  const { t } = useTranslation();
  const channelId = useDataStore((s) => s.currentChannelId);
  const guildId = useDataStore((s) => s.currentGuildId);
  const guild = useDataStore((s) => (guildId ? s.guilds[guildId] : undefined));
  const dmChannel = useDataStore((s) => (channelId ? s.dmChannels[channelId] : undefined));
  const isDirectOneToOne = dmChannel?.type === 'DM';
  const channel = useMemo(
    () => guild?.channels.find((c) => c.id === channelId) ?? dmChannel ?? null,
    [guild, channelId, dmChannel],
  );
  const floatingComposer = !!channel && channel.type !== 'VOICE';
  const { dockRef, bottomInset } = useComposerInset(floatingComposer, channelId);
  const surfaceStyle: CSSProperties & { '--chat-composer-inset': string } = {
    '--chat-composer-inset': `${bottomInset}px`,
  };
  const messages = useDataStore((s) => (channelId ? s.messagesByChannel[channelId] : undefined));
  const mergeMessages = useDataStore((s) => s.mergeMessages);
  const prependMessages = useDataStore((s) => s.prependMessages);
  const noteChannelOpened = useDataStore((s) => s.noteChannelOpened);
  const markChannelRead = useDataStore((s) => s.markChannelRead);
  const clearChannelUnread = useDataStore((s) => s.clearChannelUnread);
  const lastReadId = useDataStore((s) =>
    channelId ? s.lastReadByChannel[channelId] ?? null : null,
  );
  const me = useAuthStore((s) => s.user);
  // Shared, version-aware roster fetch (deduped with the member panel, channel
  // list, user card, etc.).
  const { members: guildMembers, roles: guildRoles } = useGuildRoster(guildId);

  // Reply target — currently-active "replying to" message, set by the
  // toolbar's reply button on any row, cleared when sent or via the
  // banner's ✕. Lives at this level so the Composer can read it.
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);

  // Older-history paging, per channel. `atStart` means the server had nothing
  // more to give — a short page is the only honest proof of that, and it's what
  // stops the scroll handler from asking again forever.
  const [older, setOlder] = useState<{ loading: boolean; atStart: boolean }>({
    loading: false,
    atStart: false,
  });
  const [historyLoad, setHistoryLoad] = useState<{ channelId: string; state: 'loading' | 'ready' | 'error' } | null>(null);
  const historyRequest = useRef(0);

  const olderEpoch = useRef(0);
  const olderFlight = useRef(false);
  const activeChannelRef = useRef(channelId);
  activeChannelRef.current = channelId;
  // If user switches channels, drop any pending reply and invalidate old loads.
  useEffect(() => {
    setReplyingTo(null);
    setOlder({ loading: false, atStart: false });
    olderFlight.current = false;
    return () => { olderEpoch.current += 1; };
  }, [channelId]);

  // Stable reference so the memoized message rows (VirtualRow) aren't
  // invalidated on every ContentPane render (typing / presence / scroll state).
  // setReplyingTo is a permanently-stable useState setter.
  const onReply = useCallback((m: Message) => setReplyingTo(m), []);

  /** One page further back. The store is newest-first, so the oldest loaded
   *  message is the last element and its id is the cursor. */
  const onLoadOlder = useCallback(async () => {
    if (!channelId || olderFlight.current) return false;
    const epoch = olderEpoch.current;
    const list = useDataStore.getState().messagesByChannel[channelId] ?? [];
    const oldest = list[list.length - 1];
    if (!oldest || oldest.id.startsWith('tmp-')) return false;
    olderFlight.current = true;
    setOlder((o) => (o.loading ? o : { ...o, loading: true }));
    try {
      const res = await api.listMessages(channelId, { before: oldest.id, limit: HISTORY_PAGE });
      if (epoch !== olderEpoch.current || channelId !== activeChannelRef.current) return false;
      prependMessages(channelId, res.messages);
      setOlder({ loading: false, atStart: atStartAfterPage(res.messages.length) });
      return true;
    } catch (err) {
      if (epoch !== olderEpoch.current || channelId !== activeChannelRef.current) return false;
      // Say it out loud: a silent failure here is indistinguishable from
      // «this is the beginning of the channel».
      setOlder((o) => ({ ...o, loading: false }));
      toast.error(...errToToast(err, t('messages.historyError')));
      return false;
    } finally {
      if (epoch === olderEpoch.current) olderFlight.current = false;
    }
  }, [channelId, prependMessages, t]);

  const loadHistory = useCallback((id: string) => {
    const request = ++historyRequest.current;
    setHistoryLoad({ channelId: id, state: 'loading' });
    void api.listMessages(id, { limit: HISTORY_PAGE }).then((res) => {
      if (request !== historyRequest.current || id !== activeChannelRef.current) return;
      // Keep socket pushes and pending optimistic messages that arrived while loading.
      mergeMessages(id, res.messages);
      setOlder({ loading: false, atStart: atStartAfterPage(res.messages.length) });
      noteChannelOpened(id, res.messages[0]?.id ?? null);
      setHistoryLoad({ channelId: id, state: 'ready' });
    }).catch((err) => {
      if (request !== historyRequest.current || id !== activeChannelRef.current) return;
      setHistoryLoad({ channelId: id, state: 'error' });
      // A visible retry replaces the toast when there is no history on screen.
      if (useDataStore.getState().messagesByChannel[id]?.length) {
        toast.error(...errToToast(err, t('messages.loadFailed')));
      }
    });
  }, [mergeMessages, noteChannelOpened, t]);

  // Load history + (re)join WS room when channel changes.
  useEffect(() => {
    if (!channelId) return;
    // Opening a channel = it's read now. Drop any pending unread badge.
    clearChannelUnread(channelId);
    // Subscribe BEFORE fetching history: joining afterwards left a window
    // where the snapshot was already taken but pushes weren't flowing yet, so
    // a message sent in that gap was lost until the channel was re-opened.
    const socket = getSocket();
    if (!socket.connected) socket.connect();
    joinChannelRoom(channelId);
    loadHistory(channelId);
    return () => {
      historyRequest.current += 1;
      // On leave, advance the cutoff to the newest message we know about so
      // returning to the channel doesn't show an obsolete "НОВОЕ" marker.
      const latest = useDataStore.getState().messagesByChannel[channelId]?.[0]?.id ?? null;
      markChannelRead(channelId, latest);
      socket.emit('channel:leave', { channelId });
    };
  }, [channelId, loadHistory, markChannelRead, clearChannelUnread]);

  // Read receipts (1:1 DMs only — the only place ✓/✓✓ is drawn): hydrate the
  // peer's pointer once there is history; live moves arrive over the socket.
  const hasHistory = !!messages?.length;
  useEffect(() => {
    if (!channelId || !isDirectOneToOne || !hasHistory) return;
    let cancelled = false;
    void api
      .listReadMarkers(channelId)
      .then((r) => {
        if (!cancelled) useDataStore.getState().setReadMarkers(channelId, r.markers);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [channelId, isDirectOneToOne, hasHistory]);

  // Advance our server-side read pointer: my badges on other tabs / devices
  // and after a reload, and the *peer's* ✓✓ in a 1:1 DM (the server only moves
  // it forward and respects saveReadHistory). Only what was on screen counts (a
  // background tab used to «read» everything). Throttled: 500 ms in a 1:1 DM,
  // where the peer watches the ✓✓; 10 s in server channels and group DMs, where
  // every viewer marks and nobody else sees it. Leaving the channel or hiding
  // the page sends what was seen at once. No marks where I may not read the
  // history — the server would ignore them anyway.
  const newestMessageId = messages && messages.length ? messages[0]!.id : null;
  const canMarkRead = !!dmChannel || canIn(channel, PERMISSIONS.READ_MESSAGE_HISTORY);
  const readMarkDelay = isDirectOneToOne ? READ_MARK_DELAY_MS : SHARED_READ_MARK_DELAY_MS;
  const readMarks = useMemo(
    () =>
      createReadMarkScheduler(
        (cid, mid) => void api.markRead(cid, mid).catch(() => {}),
        () => document.visibilityState === 'visible',
      ),
    [],
  );
  useEffect(() => {
    if (!channelId || !newestMessageId || !canMarkRead) return;
    readMarks.note(channelId, newestMessageId, readMarkDelay);
  }, [readMarks, channelId, newestMessageId, canMarkRead, readMarkDelay]);
  useEffect(() => {
    if (!channelId || !newestMessageId || !canMarkRead) return;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') readMarks.note(channelId, newestMessageId, readMarkDelay);
      else readMarks.flush();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [readMarks, channelId, newestMessageId, canMarkRead, readMarkDelay]);
  // Leaving the channel (or the pane) sends its pending mark at once.
  useEffect(() => {
    if (!channelId) return;
    return () => readMarks.flush();
  }, [readMarks, channelId]);

  // Messages that arrived while the page was hidden badged the open channel
  // (review B1); coming back to it reads them.
  useEffect(() => {
    if (!channelId) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') clearChannelUnread(channelId);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [channelId, clearChannelUnread]);

  const directPeer = dmChannel?.directParticipants.find((p) => p.userId !== me?.id);
  // One-way DM block: if the 1:1 peer has blocked me, I can't message them —
  // swap the composer for a notice (I can still see history).
  const { isBlockedBy } = useBlockedBy();
  const iAmBlocked = dmChannel?.type === 'DM' && !!directPeer && isBlockedBy(directPeer.userId);
  // A server channel I may read but not write in (announcements, read-only
  // roles): the notice instead of a composer that could only fail.
  const readOnly = !dmChannel && !canIn(channel, PERMISSIONS.SEND_MESSAGES);
  const channelLabel =
    (dmChannel && isSelfVault(dmChannel, me?.id ?? null)
      ? t('ui.saved.title')
      : dmChannel?.type === 'GROUP_DM'
        ? dmChannel.name ||
          dmChannel.directParticipants
            .filter((participant) => participant.userId !== me?.id)
            .slice(0, 3)
            .map((participant) => participant.user.displayName || participant.user.username)
            .join(', ')
        : directPeer?.user.displayName || directPeer?.user.username) ||
    channel?.name ||
    '';

  if (!channel || !channelId) {
    return <HeroEmptyState inGuild={!!guildId} />;
  }

  const isVoice = channel.type === 'VOICE';

  return (
    <div className={`flex h-full min-h-0 flex-col${isVoice ? '' : ' chat-surface'}`} data-wallpaper={isVoice ? undefined : surfaces.chat} style={surfaceStyle}>
      {isVoice ? (
        <VoiceChannelStub
          channelId={channelId}
          name={channel.name ?? ''}
          members={guildMembers}
          roles={guildRoles}
          ownerId={guild?.ownerId ?? ''}
          voiceChannels={guild?.channels.filter((candidate) => candidate.type === 'VOICE') ?? []}
          userLimit={channel.userLimit}
          bitrateKbps={channel.bitrateKbps}
          tournament={channel.tournament === true}
        />
      ) : (
        <>
          <MessagesList
            key={channelId}
            channelId={channelId}
            bottomInset={bottomInset}
            messages={messages ?? EMPTY_MESSAGE_LIST}
            loading={historyLoad?.channelId !== channelId || historyLoad.state === 'loading' || messages === undefined}
            loadError={historyLoad?.channelId === channelId && historyLoad.state === 'error'}
            onRetry={() => loadHistory(channelId)}
            onLoadOlder={onLoadOlder}
            loadingOlder={older.loading}
            atStart={older.atStart}
            myId={me?.id ?? null}
            channelName={channelLabel}
            channelKind={asSavedChannelKind(channel.type)}
            isVault={!!dmChannel && isSelfVault(dmChannel, me?.id ?? null)}
            lastReadId={lastReadId}
            members={guildMembers}
            roles={guildRoles}
            onReply={onReply}
          />
          {/* key={channelId} forces a fresh Composer instance per channel.
              Without it, switching DM/channel reuses the same React node and
              the useState initializer for `draft` never re-fires — i.e. the
              other channel's draft would stay in the textarea. Remount also
              cleanly cancels any in-flight uploads (their abort controllers
              live in state). */}
          {/* Match the message list's adaptive width: full up to 2K, capped +
              centred only on a true ultrawide (window ≥ 2700px). */}
          <div ref={dockRef} className="chat-composer-dock mx-auto w-full shrink-0 min-[2700px]:max-w-[2000px]">
          {iAmBlocked || readOnly ? (
            <div className="shrink-0 px-4 pb-4 pt-2">
              <div className="rounded-xl bg-white/[0.03] px-4 py-3 text-center text-[13px] text-ink-muted">
                {t(iAmBlocked ? 'ui2.contentPane.blockedNotice' : 'ui2.contentPane.readOnlyNotice')}
              </div>
            </div>
          ) : (
          <Composer
            key={channelId}
            channelId={channelId}
            channelName={channelLabel}
            canAttach={!!dmChannel || canIn(channel, PERMISSIONS.ATTACH_FILES)}
            replyingTo={replyingTo}
            mentionCandidates={
              guildMembers.length > 0
                ? guildMembers
                    .filter((m) => m.userId !== me?.id)
                    .map((m) => ({
                      userId: m.userId,
                      username: m.user.username,
                      displayName: m.nickname || m.user.displayName,
                      avatarUrl: m.user.avatarUrl,
                    }))
                : dmChannel
                  ? dmChannel.directParticipants
                      .filter((p) => p.userId !== me?.id)
                      .map((p) => ({
                        userId: p.userId,
                        username: p.user.username,
                        displayName: p.user.displayName,
                        avatarUrl: p.user.avatarUrl,
                      }))
                  : []
            }
            onCancelReply={() => setReplyingTo(null)}
            onRestoreReply={(m) => setReplyingTo((cur) => cur ?? m)}
            onSent={() => setReplyingTo(null)}
          />
          )}
          </div>
        </>
      )}
    </div>
  );
}
